// crewbit-admin/src/components/schedule/WeekHeader.tsx
//
// Schedule Module Phase C C2 — Week-view top strip for admin. Week label +
// nav arrows + business-hours range + optional filter trailing.
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3.3.

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { palette, admin } from '../../styles/tokens';

export interface WeekHeaderProps {
  /** First day of the week (Sunday by convention). */
  weekStart: Date;
  businessOpen?: string;
  businessClose?: string;
  onPrev?: () => void;
  onNext?: () => void;
  onToday?: () => void;
  trailing?: React.ReactNode;
}

function formatWeekLabel(start: Date): string {
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${end.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function WeekHeader({ weekStart, businessOpen, businessClose, onPrev, onNext, onToday, trailing }: WeekHeaderProps) {
  const label = formatWeekLabel(weekStart);
  const hoursLabel = businessOpen && businessClose ? `${businessOpen}–${businessClose}` : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '12px 24px', gap: 16,
      backgroundColor: admin.surface,
      borderBottom: `1px solid ${admin.border}`,
    }}>
      <button
        type="button"
        onClick={onPrev}
        aria-label="Previous week"
        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}
      >
        <ChevronLeft size={20} color={palette.g700} />
      </button>

      <button
        type="button"
        onClick={onToday}
        aria-label={`Week of ${label}`}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 17, fontWeight: 600, color: palette.g800, padding: '4px 8px',
        }}
      >
        {label}
      </button>

      <button
        type="button"
        onClick={onNext}
        aria-label="Next week"
        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}
      >
        <ChevronRight size={20} color={palette.g700} />
      </button>

      {hoursLabel ? (
        <span style={{ fontSize: 12, color: palette.g500, marginLeft: 16 }}>
          {hoursLabel}
        </span>
      ) : null}

      {trailing ? <div style={{ marginLeft: 'auto' }}>{trailing}</div> : null}
    </div>
  );
}
